import * as React from 'react';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { FormCard } from 'components/forms/FormComponents';
import TextField from 'components/forms/TextField';
import useNeveForm from 'components/forms/NeveForm';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import { confirmSignUp, resendSignUpCode, signIn, signOut } from 'aws-amplify/auth';
import { linkNeveUser } from 'lib/link-neve-user';
import { AuthContext } from 'contextProviders/AuthContext';
import { useNavigate } from 'react-router-dom';
import strings from 'strings/strings.json';
import { HomepageContext } from './Homepage';
import { Headline2 } from 'components/Elements';
import Button from 'components/buttons/Button';
import { TurnstileWidget } from './TurnstileWidget';

type ConfirmSignUpFormInput = {
    username: string;
    code: string;
};

export function ConfirmSignUpForm(): JSX.Element {
    const navigate = useNavigate();
    const { refreshUserData } = React.useContext(AuthContext);
    const { sharedData } = React.useContext(HomepageContext);

    const [error, setError] = React.useState('');
    const [resendCodeMessage, setResendCodeMessage] = React.useState(false);
    const [submittingConfirm, setSubmittingConfirm] = React.useState(false);
    const [submittingResend, setSubmittingResend] = React.useState(false);
    const [disableResendButton, setDisableResendButton] = React.useState(false);
    const [disableConfirmSignUpButton, setDisableConfirmSignUpButton] = React.useState(false);

    // Turnstile State
    const [turnstileToken, setTurnstileToken] = React.useState('');
    const [turnstileError, setTurnstileError] = React.useState('');
    const [turnstileResetSignal, setTurnstileResetSignal] = React.useState(0);
    const resetTurnstile = React.useCallback(() => setTurnstileResetSignal((prev) => prev + 1), []);
    const handleTurnstileTokenChange = React.useCallback((token: string) => setTurnstileToken(token), []);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<ConfirmSignUpFormInput>();

    React.useEffect(() => {
        if (submittingConfirm) {
            setError('');
            setResendCodeMessage(false);
        }
        setDisableResendButton(submittingConfirm);
    }, [submittingConfirm]);

    React.useEffect(() => {
        if (submittingResend) {
            setError('');
            setResendCodeMessage(false);
        }
        setDisableConfirmSignUpButton(submittingResend);
    }, [submittingResend]);

    async function createOrLinkAccount(emailAddress: string, password: string): Promise<void> {
        await signOut().catch(() => undefined);  // clear any stale local storage state
        await signIn({
            username: emailAddress,
            password: password,
            options: {
                clientMetadata: {
                    turnstileToken,
                    turnstileAction: 'signin',
                },
            },
        });

        await linkNeveUser(emailAddress);
        await refreshUserData();
    }

    async function submitConfirmationCode(data: ConfirmSignUpFormInput) {
        if (!turnstileToken) return;

        try {
            setSubmittingConfirm(true);
            await confirmSignUp({
                username: sharedData.emailAddress,
                confirmationCode: data.code,
            });

            await createOrLinkAccount(sharedData.emailAddress, sharedData.password);

            navigate('/');
        } catch (error: any) {
            console.error('Error confirming sign up:', error);
            resetTurnstile(); // Reset challenge on failure
            switch (error.name) {
                case 'NotAuthorizedException':
                    setError(strings.homepage.errors.confirmSignUp.notAuthorizedException);
                    break;
                case 'CodeMismatchException':
                    setError(strings.homepage.errors.confirmSignUp.codeMismatchException);
                    break;
                case 'ExpiredCodeException':
                    setError(strings.homepage.errors.confirmSignUp.expiredCodeException);
                    break;
                case 'UserNotFoundException':
                    setError(strings.homepage.errors.confirmSignUp.userNotFoundException);
                    break;
                case 'LimitExceededException':
                    setError(strings.homepage.errors.confirmSignUp.limitExceededException);
                    break;
                default:
                    setError(strings.homepage.errors.confirmSignUp.general);
                    break;
            }
        } finally {
            setSubmittingConfirm(false);
        }
    }

    async function resendCode() {
        try {
            setSubmittingResend(true);
            await resendSignUpCode({ username: sharedData.emailAddress });
            setResendCodeMessage(true);
        } catch (error: any) {
            if (error.name === 'InvalidParameterException' && error.message.includes('User is already confirmed')) {
                setError(strings.homepage.errors.resendSignUpCode.invalidParameterException);
            } else {
                setError(strings.homepage.errors.resendSignUpCode.general);
            }
        } finally {
            setSubmittingResend(false);
        }
    }

    return (
        <FormCard onSubmit={handleSubmit(submitConfirmationCode)}>
            <Headline2>{strings.homepage.headlines.confirmSignUp}</Headline2>
            <Notification
                notificationType={NotificationType.INFO}
                maxWidth={'100%'}
                title={`${strings.homepage.confirmSignUpInfo.neveSentCodeToEmail}: ${sharedData.emailAddress}.\n${strings.homepage.confirmSignUpInfo.enterCodeBelow}`}
            />
            <TextField
                fieldName={strings.homepage.fields.code}
                labelText={strings.homepage.fields.code}
                inputAria={strings.homepage.fields.code}
                required
                errorMessage={errors.code?.message}
                {...register('code', {
                    required: {
                        value: true,
                        message: requiredFieldErrorMessage(strings.homepage.fields.code),
                    },
                    maxLength: {
                        value: 6,
                        message: createErrorMessage(strings.homepage.fields.code, fieldErrorCodes.maxLength),
                    },
                })}
            />
            
            <TurnstileWidget
                action="signin"
                onTokenChange={handleTurnstileTokenChange}
                onError={setTurnstileError}
                resetSignal={turnstileResetSignal}
            />
            {turnstileError && (
                <Notification
                    title={turnstileError}
                    notificationType={NotificationType.DANGER}
                    maxWidth={'100%'}
                />
            )}

            <Button
                title={strings.homepage.buttons.confirmSignUp}
                aria={strings.homepage.buttons.confirmSignUp}
                type="submit"
                fullwidth
                submitting={submittingConfirm}
                disabled={disableConfirmSignUpButton || !turnstileToken}
            />
            <Button
                title={strings.homepage.buttons.resendCode}
                aria={strings.homepage.buttons.resendCode}
                onClick={resendCode}
                fullwidth
                submitting={submittingResend}
                disabled={disableResendButton}
            />
            {error && <Notification notificationType={NotificationType.DANGER} maxWidth={'100%'} title={error} />}
            {resendCodeMessage && (
                <Notification
                    notificationType={NotificationType.INFO}
                    maxWidth={'100%'}
                    title={`${strings.homepage.confirmSignUpNotification}: ${sharedData.emailAddress}`}
                />
            )}
        </FormCard>
    );
}
